import type { FC, ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import pLimit from 'p-limit'
import {
  useMutation as useConnectMutation,
  useTransport,
} from '@connectrpc/connect-query'
import type { MessageDescriptor } from '@lingui/core'
import { msg, plural, t } from '@lingui/core/macro'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { useBrandName } from '@jotta/grpc-connect-client/config'
import { authTransport } from '@jotta/grpc-connect-client/transport'
import { Album_AlbumAction } from '@jotta/grpc-connect-openapi/album'
import {
  addPhotosToAlbum as addPhotosToAlbumDescriptor,
  deleteAlbum as deleteAlbumDescriptor,
  followAlbum as followAlbumDescriptor,
  removePhotosFromAlbum as removePhotosFromAlbumDescriptor,
  setCoverPhoto as setCoverPhotoDescriptor,
  sharePhotos as sharePhotosDescriptor,
  unshareAlbum as unshareAlbumDescriptor,
} from '@jotta/grpc-connect-openapi/albumQuery'
import {
  PatchPhotoRequest_StateChange,
  Photo_PhotoAction,
  type PhotoRef,
} from '@jotta/grpc-connect-openapi/photo'
import { deletePhoto as deletePhotoDescriptor } from '@jotta/grpc-connect-openapi/photoQuery'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import SvgPhoto from '@jotta/ui/SvgPhoto'
import { dismissToast, showToast } from '@jotta/ui/ToastProvider'
import { popPath } from '@jotta/utils/pathUtils'

import { downloadPhotos } from '../../api/photosApi'
import {
  getAlbumQuery,
  invalidateAlbumMetadataQueries,
  invalidateAlbumQueries,
  useAlbum,
} from '../../api/useAlbum'
import type { CollectionRef } from '../../api/useCollection'
import { useAlbumId, useCollection } from '../../api/useCollection'
import { usePatchPhotos } from '../../api/usePatchPhotos'
import {
  resolveSelectablePhoto,
  usePhotoFetcher,
  usePhotoRef,
  usePhotos,
} from '../../api/usePhoto'
import { usePhotoStore, useSelectionStore } from '../../store/PhotoContext'
import {
  getSelectionMediaNoun,
  type SelectablePhoto,
} from '../../store/SelectionStore'
import { DeleteAlbumDialog } from '../Album/Dialogs/DeleteAlbumDialog'
import { DeletePhotoDialog } from '../Album/Dialogs/DeletePhotoDialog'
import { HidePhotoDialog } from '../Album/Dialogs/HidePhotoDialog'
import { LeaveAlbumDialog } from '../Album/Dialogs/LeaveAlbumDialog'
import { LoginToContinueDialog } from '../Album/Dialogs/LoginToContinueDialog'
import { RemovePhotoDialog } from '../Album/Dialogs/RemovePhotoDialog'
import { SelectAlbumDialog } from '../Album/Dialogs/SelectAlbumDialog'
import { ShareAlbumDialog } from '../Album/Dialogs/ShareAlbumDialog'
import { SharePhotosDialog } from '../Album/Dialogs/SharePhotosDialog'
import { useAddPhotoMode } from '../PhotoGallery/useAddPhotoMode'

import { photoActionsApi } from './PhotoActions.config'
import type { PhotoActionType } from './PhotoActions.types'

type ActionLabel =
  | MessageDescriptor
  | string
  | ((count: number) => MessageDescriptor | string)

type Action = {
  icon: BrandThemeIcon
  label: ActionLabel
  testid?: string
}
/** What an action outside this module hands `ActionDispatcher` to describe its button. */
export type { Action as PhotoActionDefinition }
type ActionPhoto = SelectablePhoto & { fileName?: string }

export type ActionDispatcherProps = {
  onClick: () => void
  loading: boolean
  label: string
  icon: BrandThemeIcon
  testid: string | undefined
}

const {
  ADD_PHOTOS,
  ADD_PHOTOS_TO_ALBUM,
  REMOVE_PHOTOS_FROM_ALBUM,
  SHARE_PHOTO,
  DELETE_PHOTOS,
  HIDE_PHOTO,
  UNHIDE_PHOTO,
  FOLLOW_ALBUM,
  LEAVE_ALBUM,
  SHARE_ALBUM,
  DELETE_ALBUM,
  DOWNLOAD_PHOTOS,
} = photoActionsApi

function setCoverPhotoAction(): Action {
  return {
    icon: SvgPhoto,
    label: msg`Set as cover photo`,
    testid: 'ACTION_SET_COVER_PHOTO',
  }
}

function downloadPhotosAction(photos: readonly SelectablePhoto[]): Action {
  return {
    icon: DOWNLOAD_PHOTOS.icon,
    label: count => {
      if (count <= 1) {
        return DOWNLOAD_PHOTOS.label
      }
      switch (getSelectionMediaNoun(photos)) {
        case 'videos':
          return t`Download ${count} videos`
        case 'items':
          return t`Download ${count} items`
        default:
          return t`Download ${count} photos`
      }
    },
    testid: DOWNLOAD_PHOTOS.testid,
  }
}

const removePhotosFromAlbumAction: Action = {
  icon: REMOVE_PHOTOS_FROM_ALBUM.icon,
  label: count =>
    count > 1 ? t`Remove ${count} items` : REMOVE_PHOTOS_FROM_ALBUM.label,
  testid: REMOVE_PHOTOS_FROM_ALBUM.testid,
}

function translateLabel(action: Action, count = 1) {
  const label =
    typeof action.label === 'function' ? action.label(count) : action.label
  return typeof label === 'string' ? label : t(label)
}

type ContextActionDispatcherProps = {
  loading: boolean
  onClick: () => void
  action: Action
}

// Module-level so its identity is stable: a component created inside the provider's
// render would be a new type on every render and remount every action button.
function ContextActionDispatcher({
  action,
  ...props
}: ContextActionDispatcherProps) {
  const { photos, actionDispatcherComponent: ActionDispatcherComponent } =
    usePhotoActionContext()
  return (
    <ActionDispatcherComponent
      {...props}
      label={translateLabel(action, photos.length)}
      icon={action.icon}
      testid={action.testid}
    />
  )
}

const PhotoActionContext = createContext<
  | {
      collectionRef: CollectionRef
      photos: readonly SelectablePhoto[]
      photoIds: readonly string[]
      hasMd5OnlyPhotos: boolean
      resolveActionPhotos: () => Promise<readonly ActionPhoto[]>
      ActionDispatcher: FC<ContextActionDispatcherProps>
      actionDispatcherComponent: FC<ActionDispatcherProps>
      setActiveActionHandler: (handler: FC, onSuccess?: () => void) => void
      clearActiveAction: (success: boolean) => void
      trackAction: (actionType: PhotoActionType) => void
    }
  | undefined
>(undefined)
/** The seam an action defined outside this module connects itself to. */
export function usePhotoActionContext() {
  const context = useContext(PhotoActionContext)
  if (!context) {
    throw new Error('must be used within PhotoActionProvider')
  }
  return context
}

function PhotoActionProvider({
  collectionRef,
  photos,
  actionDispatcherComponent,
  hasMd5OnlyPhotos = false,
  onTrackAction,
  children,
}: {
  collectionRef: CollectionRef
  photos: readonly SelectablePhoto[]
  actionDispatcherComponent: FC<ActionDispatcherProps>
  /**
   * Set when an md5 is all the photos carry: they identify themselves by it, have no
   * fetched photo behind them and are not addressable in `collectionRef`, so their
   * actions can be neither fetched nor gated up front and whoever composes them vouches
   * for what they offer.
   */
  hasMd5OnlyPhotos?: boolean
  onTrackAction?: (actionType: PhotoActionType) => void
  children?: ReactNode
}) {
  const [ActiveActionHandler, setActiveActionHandler] = useState<
    FC | undefined
  >()
  const [onSuccess, setOnSuccess] = useState<() => void>()
  const [addPhotoModeAlbumId] = useAddPhotoMode()
  const transport = useTransport()
  const queryClient = useQueryClient()
  const resolveActionPhotos = useCallback(async () => {
    if (!hasMd5OnlyPhotos) {
      return photos
    }

    return Promise.all(
      photos.map(photo =>
        photo.encodedContentRef
          ? photo
          : resolveSelectablePhoto(transport, queryClient, photo),
      ),
    )
  }, [hasMd5OnlyPhotos, photos, queryClient, transport])

  return (
    <PhotoActionContext.Provider
      value={{
        collectionRef,
        photos,
        photoIds: photos.map(photo => photo.id),
        hasMd5OnlyPhotos,
        resolveActionPhotos,
        ActionDispatcher: ContextActionDispatcher,
        actionDispatcherComponent,
        setActiveActionHandler: (handler, onSuccessHandler) => {
          setActiveActionHandler(() => handler)
          setOnSuccess(() => onSuccessHandler)
        },
        clearActiveAction: success => {
          setActiveActionHandler(undefined)
          if (success && onSuccess) {
            onSuccess()
          }
        },
        trackAction: actionType => onTrackAction?.(actionType),
      }}
    >
      {ActiveActionHandler && <ActiveActionHandler />}
      {addPhotoModeAlbumId ? (
        <AddPhotosHandler albumId={addPhotoModeAlbumId} />
      ) : (
        <div className="flex gap-2">{children}</div>
      )}
    </PhotoActionContext.Provider>
  )
}

function isApplicableToAllPhotos(
  action: Photo_PhotoAction,
  photos: readonly { actions: readonly Photo_PhotoAction[] }[],
) {
  return (
    photos.length > 0 && photos.every(photo => photo.actions.includes(action))
  )
}

/**
 * The photos with their actions, if every one carries them. Only reads that ask the
 * server for actions set them: Timeline v2's list and the photo the media carousel
 * fetches. Other lists (Timeline v1, albums, search) leave them out, so an empty array
 * really means "no actions".
 */
export function photosWithActions(
  photos: readonly SelectablePhoto[],
): (SelectablePhoto & { actions: Photo_PhotoAction[] })[] | undefined {
  return photos.length > 0 &&
    photos.every(photo => Array.isArray(photo.actions))
    ? (photos as (SelectablePhoto & { actions: Photo_PhotoAction[] })[])
    : undefined
}

/**
 * The selected photos to decide which actions to offer, and to hide or unhide.
 *
 * When the list already carries every selected photo's actions, those are used and
 * nothing is requested, so the bar shows its actions at once however many photos are
 * selected. Otherwise each photo is fetched, as before.
 */
function useGatePhotos({ enabled = true }: { enabled?: boolean } = {}) {
  const { collectionRef, photos, photoIds } = usePhotoActionContext()
  const fromList = useMemo(() => photosWithActions(photos), [photos])
  const fetched = usePhotos(collectionRef, photoIds, {
    enabled: enabled && !fromList,
  })
  return fromList ?? fetched
}

/**
 * What `usePatchPhotos` needs, from listed or fetched photos alike: fetched photos name
 * their legacy id in `idDeprecated`, listed photos in `id`.
 */
function toPatchPhotos(
  photos: readonly { id: string; idDeprecated?: string; ref?: PhotoRef }[],
) {
  return photos.map(({ id, idDeprecated, ref }) => ({
    idDeprecated: idDeprecated || id,
    ref,
  }))
}

/**
 * The photos with the ref they were read with, or nothing if they were not all read with
 * their actions (see `photosWithActions`) or one has no ref. Lets an action address the
 * photos without fetching each one first.
 */
function listedPhotosWithRefs(photos: readonly SelectablePhoto[]) {
  const listed = photosWithActions(photos)
  return listed?.every((photo): photo is typeof photo & { ref: PhotoRef } =>
    Boolean(photo.ref?.ref.case),
  )
    ? listed
    : undefined
}

/** At most this many photo mutations at once: there is no batch RPC for these. */
const mutationLimit = pLimit(6)

function getPhotoRefMd5(photo: SelectablePhoto) {
  return photo.ref?.ref.case === 'md5' ? photo.ref.ref.value : undefined
}

/**
 * The md5 a photo was read with, if it was read with its actions (see
 * `photosWithActions`): those reads also ask for the current md5. The md5 legacy timeline
 * and album media carry may be stale, so it is not taken. An empty md5 is the default of
 * a field the read did not ask for.
 */
function getListedPhotoMd5(photo: SelectablePhoto) {
  return Array.isArray(photo.actions) && photo.md5 ? photo.md5 : undefined
}

function getPhotoMd5(
  photo: SelectablePhoto,
  { allowMd5Only = false }: { allowMd5Only?: boolean } = {},
) {
  return allowMd5Only
    ? (photo.md5 ?? getPhotoRefMd5(photo))
    : (getPhotoRefMd5(photo) ?? getListedPhotoMd5(photo))
}

/**
 * The md5 of every photo, or nothing if any of them cannot produce one: an action
 * addressing photos by md5 would otherwise act on the wrong set. A photo produces the md5
 * in its ref, or the one its list gave it (see `getListedPhotoMd5`). `allowMd5Only` takes
 * the md5 a photo names itself by, for photos that never carried a ref to read one from.
 */
function getContextPhotoMd5s(
  photos: readonly SelectablePhoto[],
  options?: { allowMd5Only?: boolean },
) {
  const photoMd5s = photos.map(photo => getPhotoMd5(photo, options))
  return photoMd5s.every((md5): md5 is string => typeof md5 === 'string')
    ? photoMd5s
    : undefined
}

const deletePhotosToastId = 'delete-photos'

function DeletePhotosHandler() {
  const { clearActiveAction, collectionRef, photos, photoIds } =
    usePhotoActionContext()
  const { fetchPhoto } = usePhotoFetcher(collectionRef)
  const { mutateAsync: deletePhoto } = useConnectMutation(deletePhotoDescriptor)
  const photoStore = usePhotoStore()
  const selectionStore = useSelectionStore()
  const { mutate: deletePhotos, isPending } = useMutation({
    onMutate: () => {
      showToast({
        id: deletePhotosToastId,
        message: t`Deleting photos...`,
        isLoading: true,
      })
    },
    onSuccess: () => {
      dismissToast(deletePhotosToastId)
      selectionStore.delete(...photoIds)
      clearActiveAction(true)
    },
    onError: () => {
      dismissToast(deletePhotosToastId)
    },
    mutationFn: () => {
      const deleteOne = async (photoId: string, ref: PhotoRef | undefined) => {
        await deletePhoto({ ref })
        await photoStore.timeline.delete([photoId])
      }
      const listed = listedPhotosWithRefs(photos)
      return Promise.all(
        listed
          ? listed.map(photo =>
              mutationLimit(() => deleteOne(photo.id, photo.ref)),
            )
          : photoIds.map(photoId =>
              fetchPhoto(photoId).then(photo => deleteOne(photoId, photo.ref)),
            ),
      )
    },
  })
  return (
    <DeletePhotoDialog
      open
      isLoading={isPending}
      count={photoIds.length}
      onDeletePhoto={deletePhotos}
      onClose={open => clearActiveAction(false)}
    />
  )
}

function SharePhotosHandler() {
  const {
    collectionRef,
    photos,
    photoIds,
    clearActiveAction,
    trackAction,
    hasMd5OnlyPhotos,
  } = usePhotoActionContext()
  const { fetchAll } = usePhotoFetcher(collectionRef)
  const {
    data: sharePhotosResponse,
    mutateAsync: sharePhotos,
    reset,
  } = useConnectMutation(sharePhotosDescriptor, {
    transport: authTransport,
  })
  const { mutate: fetchAndSharePhotos, isPending: isPendingSharing } =
    useMutation({
      mutationFn: async () => {
        const photoMd5s =
          getContextPhotoMd5s(photos, { allowMd5Only: hasMd5OnlyPhotos }) ??
          (await fetchAll(photoIds)).map(photo => photo.md5)
        await sharePhotos({
          photoMd5s,
        })
        trackAction('SHARE_PHOTO')
      },
    })
  const { mutate: unshareAlbum, isPending: isPendingUnsharing } =
    useConnectMutation(unshareAlbumDescriptor, {
      transport: authTransport,
      onSuccess: () => reset(),
    })

  const { albumId, shareId } = sharePhotosResponse || {}
  return (
    <SharePhotosDialog
      forceOpen={true}
      photoCount={photoIds.length}
      shareId={shareId}
      isLoading={isPendingSharing || isPendingUnsharing}
      onSharePhoto={shared => {
        if (shared) {
          fetchAndSharePhotos()
        } else if (albumId) {
          unshareAlbum({
            albumId: albumId,
          })
        }
      }}
      triggerElement={undefined}
      onOpenChange={open => {
        if (!open) {
          clearActiveAction(false)
        }
      }}
    />
  )
}

function DownloadPhotos() {
  const {
    ActionDispatcher,
    photos,
    trackAction,
    hasMd5OnlyPhotos,
    resolveActionPhotos,
  } = usePhotoActionContext()
  const brandName = useBrandName()
  const authenticated = useAuthenticated() === 'TRUE'
  const knownFileName =
    photos.length === 1
      ? (photos[0] as ActionPhoto | undefined)?.fileName
      : undefined
  const firstRef =
    !hasMd5OnlyPhotos && photos.length === 1 && !knownFileName
      ? photos[0].ref
      : undefined
  const { data: firstPhoto } = usePhotoRef(firstRef, {
    enabled: authenticated && !hasMd5OnlyPhotos,
    throwOnError: false,
  })
  const firstFileName = knownFileName || firstPhoto?.fileName
  const defaultFileName = firstFileName
    ? firstFileName
    : brandName
      ? brandName
      : t`Photos`
  const { mutate, isPending } = useMutation({
    throwOnError: false,
    mutationFn: async () => {
      const actionPhotos = await resolveActionPhotos()
      const refs = actionPhotos.flatMap(({ encodedContentRef }) =>
        encodedContentRef ? [encodedContentRef] : [],
      )

      if (refs.length !== actionPhotos.length) {
        throw new Error('Could not load content refs for selected photos')
      }

      const fileName =
        actionPhotos.length === 1 && actionPhotos[0].fileName
          ? actionPhotos[0].fileName
          : defaultFileName

      return downloadPhotos({
        fileName,
        refs,
      })
    },
    onSuccess: () => {
      trackAction('DOWNLOAD_PHOTOS')
    },
    onError: () => {
      showToast({
        message: t`Downloading wasn't successful.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
  })
  return (
    <ActionDispatcher
      action={downloadPhotosAction(photos)}
      loading={isPending}
      onClick={() => mutate()}
    />
  )
}

function HidePhotoHandler() {
  const { collectionRef, photoIds, clearActiveAction } = usePhotoActionContext()
  const photos = useGatePhotos()
  const selectionStore = useSelectionStore()
  const { mutate: patchPhotos, isPending } = usePatchPhotos({
    collectionRef,
    onSuccess: () => {
      selectionStore.delete(...photoIds)
      clearActiveAction(true)
    },
  })
  return (
    isApplicableToAllPhotos(Photo_PhotoAction.HIDE, photos) && (
      <HidePhotoDialog
        open
        count={photos.length}
        isLoading={isPending}
        onClose={() => clearActiveAction(false)}
        onHidePhoto={() => {
          patchPhotos({
            photos: toPatchPhotos(photos),
            stateChange: PatchPhotoRequest_StateChange.HIDE,
          })
        }}
      />
    )
  )
}

function UnhidePhotos({ onSuccess }: { onSuccess?: () => void }) {
  const { collectionRef, ActionDispatcher, photoIds, clearActiveAction } =
    usePhotoActionContext()
  const photos = useGatePhotos()
  const selectionStore = useSelectionStore()
  const { mutate: patchPhotos, isPending } = usePatchPhotos({
    collectionRef,
    onSuccess: () => {
      selectionStore.delete(...photoIds)
      clearActiveAction(true)
      onSuccess?.()
    },
  })
  return (
    isApplicableToAllPhotos(Photo_PhotoAction.UNHIDE, photos) && (
      <ActionDispatcher
        action={UNHIDE_PHOTO}
        loading={isPending}
        onClick={() =>
          patchPhotos({
            photos: toPatchPhotos(photos),
            stateChange: PatchPhotoRequest_StateChange.UNHIDE,
          })
        }
      />
    )
  )
}

function AddToAlbumHandler() {
  const { clearActiveAction, collectionRef } = usePhotoActionContext()
  const { mutate } = useAddPhotos()
  const currentAlbumId = useAlbumId(collectionRef)
  return (
    <SelectAlbumDialog
      open
      onClose={() => clearActiveAction(false)}
      currentAlbumId={currentAlbumId}
      addToAlbumHandler={albumId => mutate(albumId)}
    />
  )
}

function RemoveFromAlbum({ onSuccess }: { onSuccess?: () => void }) {
  const { collectionRef } = usePhotoActionContext()
  const albumId = useAlbumId(collectionRef)
  if (!albumId) {
    return null
  }
  return (
    <PhotoActionTrigger
      actionType={Photo_PhotoAction.REMOVE_FROM_ALBUM}
      action={removePhotosFromAlbumAction}
      handler={() => <RemoveFromAlbumHandler albumId={albumId} />}
      onSuccess={onSuccess}
    />
  )
}

function RemoveFromAlbumHandler({ albumId }: { albumId: string }) {
  const { clearActiveAction, collectionRef, photos, photoIds } =
    usePhotoActionContext()
  const { fetchAll } = usePhotoFetcher(collectionRef)
  const selectionStore = useSelectionStore()
  const { mutateAsync: removePhotosFromAlbum } = useConnectMutation(
    removePhotosFromAlbumDescriptor,
  )
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const photoRefs =
        listedPhotosWithRefs(photos)?.map(photo => photo.ref) ??
        (await fetchAll(photoIds)).map(({ ref }) => {
          if (!ref) {
            throw new Error('could not load photo ref for photo')
          }
          return ref
        })
      await removePhotosFromAlbum({
        albumId,
        photoRefs,
      })
    },
    onSuccess: async () => {
      try {
        await invalidateAlbumQueries(albumId)
      } finally {
        selectionStore.delete(...photoIds)
        clearActiveAction(true)
      }
    },
  })
  return (
    <RemovePhotoDialog
      open
      isLoading={isPending}
      count={photoIds.length}
      onClose={() => clearActiveAction(false)}
      onRemovePhoto={() => mutate()}
    />
  )
}

function UseAsCoverPhoto({
  albumId,
  canUpdateCoverPhoto: canUpdateCoverPhotoProp,
}: {
  albumId: string
  canUpdateCoverPhoto?: boolean
}) {
  const { ActionDispatcher, photoIds } = usePhotoActionContext()
  const { data: album } = getAlbumQuery({
    variables: {
      albumIdOrShareId: albumId,
    },
    enabled: canUpdateCoverPhotoProp === undefined && Boolean(albumId),
    throwOnError: false,
    select: data => data.album,
  })
  const canUpdateCoverPhoto =
    canUpdateCoverPhotoProp ??
    album?.actions?.includes(Album_AlbumAction.UPDATE_COVER_PHOTO) ??
    false
  const photoId = photoIds[0]
  const { mutate, isPending } = useConnectMutation(setCoverPhotoDescriptor, {
    onSuccess: async (_data, variables) => {
      const updatedAlbumId = variables.albumId
      if (!updatedAlbumId) {
        return
      }

      await invalidateAlbumMetadataQueries(updatedAlbumId)

      showToast({
        message: t`Album cover updated.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
    onError: () => {
      showToast({
        message: t`Updating album cover wasn't successful.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
  })

  if (photoIds.length !== 1 || !photoId || !canUpdateCoverPhoto) {
    return null
  }

  return (
    <ActionDispatcher
      action={setCoverPhotoAction()}
      loading={isPending}
      onClick={() =>
        mutate({
          albumId,
          photoId,
        })
      }
    />
  )
}

function PhotoActionTrigger({
  action,
  actionType,
  handler,
  onSuccess,
}: {
  action: Action
  actionType: Photo_PhotoAction
  handler: FC
  onSuccess?: () => void
}) {
  const photoActionContext = usePhotoActionContext()
  const { ActionDispatcher, setActiveActionHandler, hasMd5OnlyPhotos } =
    photoActionContext
  const photos = useGatePhotos({ enabled: !hasMd5OnlyPhotos })
  // An md5 alone carries no photo to read the available actions off, so whoever composed
  // the bar decides what it offers rather than the server.
  const isVisible =
    hasMd5OnlyPhotos || isApplicableToAllPhotos(actionType, photos)

  return (
    isVisible && (
      <ActionDispatcher
        action={action}
        loading={false}
        onClick={() => setActiveActionHandler(handler, onSuccess)}
      />
    )
  )
}

export const PhotoAction = {
  Provider: PhotoActionProvider,

  DownloadPhotos,
  UseAsCoverPhoto,
  UnhidePhotos,
  RemoveFromAlbum,

  HidePhotos: ({ onSuccess }: { onSuccess?: () => void }) => (
    <PhotoActionTrigger
      action={HIDE_PHOTO}
      actionType={Photo_PhotoAction.HIDE}
      handler={HidePhotoHandler}
      onSuccess={onSuccess}
    />
  ),

  AddToAlbum: () => (
    <PhotoActionTrigger
      actionType={Photo_PhotoAction.ADD_TO_ALBUM}
      action={ADD_PHOTOS_TO_ALBUM}
      handler={AddToAlbumHandler}
    />
  ),
  SharePhotos: () => (
    <PhotoActionTrigger
      action={SHARE_PHOTO}
      actionType={Photo_PhotoAction.SHARE}
      handler={SharePhotosHandler}
    />
  ),
  DeletePhotos: ({ onSuccess }: { onSuccess?: () => void }) => (
    <PhotoActionTrigger
      actionType={Photo_PhotoAction.DELETE}
      action={DELETE_PHOTOS}
      handler={DeletePhotosHandler}
      onSuccess={onSuccess}
    />
  ),
}

type AlbumActionDispatcherProps = ActionDispatcherProps & {
  actionType: Album_AlbumAction | undefined
}

const AlbumActionContext = createContext<
  | {
      collectionRef: CollectionRef
      ActionDispatcher: FC<{
        action: Action
        actionType: Album_AlbumAction | undefined
        loading: boolean
        onClick: () => void
      }>
      albumIdOrShareId: string
      setActiveActionHandler: (handler: FC) => void
      cancelActiveAction: () => void
    }
  | undefined
>(undefined)
function useAlbumActionContext() {
  const context = useContext(AlbumActionContext)
  if (!context) {
    throw new Error('must be used within AlbumActionProvider')
  }
  return context
}

function AlbumActionDispatcher({
  actionDispatcherComponent: ActionDispatcher,
  actionType,
  ...rest
}: AlbumActionDispatcherProps & {
  actionDispatcherComponent: FC<ActionDispatcherProps>
}) {
  const { albumIdOrShareId } = useAlbumActionContext()
  const { data } = useAlbum(albumIdOrShareId)
  const displayActionDispatcher =
    !actionType || data?.actions.includes(actionType)
  return displayActionDispatcher && <ActionDispatcher {...rest} />
}

function AlbumActionProvider({
  albumIdOrShareId,
  collectionRef,
  actionDispatcherComponent,
  children,
}: {
  albumIdOrShareId: string
  collectionRef: CollectionRef
  actionDispatcherComponent: FC<ActionDispatcherProps>
  children: ReactNode
}) {
  const [ActiveActionHandler, setActiveActionHandler] = useState<FC>()

  return (
    <AlbumActionContext.Provider
      value={{
        ActionDispatcher: props => (
          <AlbumActionDispatcher
            onClick={props.onClick}
            loading={props.loading}
            label={translateLabel(props.action)}
            icon={props.action.icon}
            testid={props.action.testid}
            actionType={props.actionType}
            actionDispatcherComponent={actionDispatcherComponent}
          />
        ),
        collectionRef,
        albumIdOrShareId,
        setActiveActionHandler: handler =>
          setActiveActionHandler(() => handler),
        cancelActiveAction: () => setActiveActionHandler(undefined),
      }}
    >
      {ActiveActionHandler && <ActiveActionHandler />}
      <div className="flex gap-2">{children}</div>
    </AlbumActionContext.Provider>
  )
}

function DownloadAlbum() {
  const brandName = useBrandName()
  const { mutate, isPending: isDownloadPending } = useMutation({
    mutationFn: downloadPhotos,
  })
  const { collectionRef, ActionDispatcher } = useAlbumActionContext()
  const { data, isPending: isContentRefPending } = useCollection(collectionRef)
  const collection = data?.case === 'collection' && data.value
  const { encodedContentRef, title } = collection || {}
  const fileName = title ? title : brandName ? brandName : t`Photos`
  const isPending = isDownloadPending || isContentRefPending
  return (
    <ActionDispatcher
      action={DOWNLOAD_PHOTOS}
      actionType={undefined}
      loading={isPending}
      onClick={() =>
        mutate({
          fileName,
          refs: encodedContentRef ? [encodedContentRef] : [],
        })
      }
    />
  )
}

function LoginToContinue({ title, url }: { url: string; title: string }) {
  const { cancelActiveAction } = useAlbumActionContext()
  return (
    <LoginToContinueDialog
      open
      title={title}
      url={url}
      onClose={cancelActiveAction}
      triggerElement={undefined}
    />
  )
}

function useAddPhotos() {
  const {
    collectionRef,
    photos,
    photoIds,
    clearActiveAction,
    trackAction,
    hasMd5OnlyPhotos,
  } = usePhotoActionContext()
  const navigate = useNavigate()
  const { fetchAll } = usePhotoFetcher(collectionRef)
  const currentAlbumId = useAlbumId(collectionRef)
  const selectionStore = useSelectionStore()
  const { mutateAsync: addPhotosToAlbum } = useConnectMutation(
    addPhotosToAlbumDescriptor,
    {
      onSuccess: ({ addedPhotoCount, albumId }) => {
        trackAction('ADD_PHOTOS_TO_ALBUM')
        clearActiveAction(true)
        selectionStore.clear()

        const autoNavigateToAlbum = albumId !== currentAlbumId
        if (autoNavigateToAlbum) {
          navigate(`/photo/album/${albumId}`)
        }

        showToast({
          hasCloseButton: true,
          message: plural(addedPhotoCount, {
            one: 'Added 1 photo',
            other: 'Added # photos',
          }),
          actionButton: autoNavigateToAlbum
            ? undefined
            : {
                label: t`Show album`,
                onClick: () => navigate(`/photo/album/${albumId}`),
              },
        })
      },
    },
  )
  return useMutation({
    mutationFn: async (albumId: string) => {
      const photoMd5s =
        getContextPhotoMd5s(photos, { allowMd5Only: hasMd5OnlyPhotos }) ??
        (await fetchAll(photoIds)).map(photo => photo.md5)
      await addPhotosToAlbum({
        album: {
          case: 'existingAlbumId',
          value: albumId,
        },
        photoMd5s,
      })
    },
  })
}

function AddPhotosHandler({ albumId }: { albumId: string }) {
  const { ActionDispatcher } = usePhotoActionContext()
  const { mutate, isPending } = useAddPhotos()
  return (
    <ActionDispatcher
      action={ADD_PHOTOS_TO_ALBUM}
      loading={isPending}
      onClick={() => mutate(albumId)}
    />
  )
}

function AddPhotos() {
  const { ActionDispatcher, albumIdOrShareId, setActiveActionHandler } =
    useAlbumActionContext()
  const navigate = useNavigate()
  const authenticated = useAuthenticated() === 'TRUE'
  const [navigating, setNavigating] = useState(false)
  return (
    <ActionDispatcher
      action={ADD_PHOTOS}
      actionType={Album_AlbumAction.ADD_PHOTOS}
      loading={navigating}
      onClick={() => {
        if (authenticated) {
          navigate(`/photo?add-photos=${albumIdOrShareId}`)
          setNavigating(true)
        } else {
          setActiveActionHandler(() => (
            <LoginToContinue
              title={t`Log in to add your own photos`}
              url={`/share/${albumIdOrShareId}/addphotos`}
            />
          ))
        }
      }}
    />
  )
}

function FollowAlbum() {
  const { ActionDispatcher, albumIdOrShareId, setActiveActionHandler } =
    useAlbumActionContext()
  const authenticated = useAuthenticated() === 'TRUE'
  const navigate = useNavigate()
  const [isNavigating, setIsNavigating] = useState(false)
  const { mutate: followAlbum, isPending } = useConnectMutation(
    followAlbumDescriptor,
    {
      onSuccess: ({ albumId }) => {
        navigate(`/photo/album/${albumId}`)
        setIsNavigating(true)
      },
    },
  )
  return (
    <ActionDispatcher
      action={FOLLOW_ALBUM}
      actionType={Album_AlbumAction.FOLLOW_ALBUM}
      loading={isPending || isNavigating}
      onClick={() => {
        if (authenticated) {
          followAlbum({
            shareId: albumIdOrShareId,
          })
        } else {
          setActiveActionHandler(() => (
            <LoginToContinue
              title={t`Would you like to follow this album?`}
              url={`/share/${albumIdOrShareId}/follow`}
            />
          ))
        }
      }}
    />
  )
}

function LeaveAlbumHandler() {
  const { albumIdOrShareId, cancelActiveAction } = useAlbumActionContext()
  const navigate = useNavigate()
  const afterLeavePath = popPath(location.pathname)
  const { mutate: leaveAlbum, isPending } = useConnectMutation(
    unshareAlbumDescriptor,
    {
      onSuccess: () => {
        navigate(afterLeavePath, {
          replace: true,
        })
      },
    },
  )
  return (
    <LeaveAlbumDialog
      open
      isLoading={isPending}
      onLeaveAlbum={() => {
        leaveAlbum({
          albumId: albumIdOrShareId,
        })
      }}
      onClose={cancelActiveAction}
    />
  )
}

function ShareAlbum() {
  const { albumIdOrShareId } = useAlbumActionContext()
  const shareId = useAlbum(albumIdOrShareId).data?.shareId
  const actionFilter = shareId
    ? undefined // if already shared, we don't need to filter on the share action
    : Album_AlbumAction.SHARE_ALBUM
  return (
    <AlbumActionTrigger
      action={SHARE_ALBUM}
      actionFilter={actionFilter}
      handler={ShareAlbumHandler}
    />
  )
}

function ShareAlbumHandler() {
  const { albumIdOrShareId, cancelActiveAction } = useAlbumActionContext()
  return (
    <ShareAlbumDialog
      forceOpen
      albumId={albumIdOrShareId}
      onOpenChange={cancelActiveAction}
      triggerElement={undefined}
    />
  )
}

function DeleteAlbumHandler() {
  const { albumIdOrShareId, cancelActiveAction } = useAlbumActionContext()
  const navigate = useNavigate()
  const location = useLocation()
  const afterDeletePath = popPath(location.pathname)

  const { mutate: deleteAlbum, isPending } = useConnectMutation(
    deleteAlbumDescriptor,
    {
      onSuccess: () =>
        navigate(
          afterDeletePath === '/share' ? '/photo/all_shared' : afterDeletePath,
        ),
    },
  )
  return (
    <DeleteAlbumDialog
      open
      onClose={cancelActiveAction}
      isLoading={isPending}
      onDeleteAlbum={() =>
        deleteAlbum({
          albumId: albumIdOrShareId,
        })
      }
    />
  )
}

function AlbumActionTrigger({
  action,
  actionFilter,
  handler,
}: {
  action: Action
  actionFilter: Album_AlbumAction | undefined
  handler: FC
}) {
  const { ActionDispatcher, setActiveActionHandler } = useAlbumActionContext()
  return (
    <ActionDispatcher
      action={action}
      actionType={actionFilter}
      loading={false}
      onClick={() => setActiveActionHandler(handler)}
    />
  )
}

export const AlbumAction = {
  Provider: AlbumActionProvider,

  AddPhotos,
  DownloadAlbum,
  ShareAlbum,
  FollowAlbum,

  DeleteAlbum: () => (
    <AlbumActionTrigger
      action={DELETE_ALBUM}
      actionFilter={Album_AlbumAction.DELETE_ALBUM}
      handler={DeleteAlbumHandler}
    />
  ),
  LeaveAlbum: () => (
    <AlbumActionTrigger
      action={LEAVE_ALBUM}
      actionFilter={Album_AlbumAction.LEAVE_ALBUM}
      handler={LeaveAlbumHandler}
    />
  ),
}
